import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '@/contexts/FirebaseContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ChatThread } from '@/types/chatTypes';
import { chatService } from '@/services/chatService';
import { getEnrollmentsByStudentId } from '@/utils/firestoreService';

interface ChatListProps {
  userId: string;
  role: 'student' | 'instructor';
}

export const ChatList: React.FC<ChatListProps> = ({ userId, role }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const { getFirestore } = useFirebase();

  const formatMessageTime = (timestamp: string | number | Date | null) => {
    if (!timestamp) return '';
      const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 7) {
      return date.toLocaleDateString();
    } else if (days > 0) {
      return `${days}d ago`;
    } else {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours > 0) {
        return `${hours}h ago`;
      } else {
        const minutes = Math.floor(diff / (1000 * 60));
        if (minutes > 0) {
          return `${minutes}m ago`;
        } else {
          return 'Just now';
        }
      }
    }
  };

  useEffect(() => {
    const loadThreads = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        let chatThreads: ChatThread[] = [];

        if (role === 'student') {
          // For students, get their enrollments and create threads for each instructor
          const enrollments = await getEnrollmentsByStudentId(userId);
          const activeEnrollments = enrollments.filter(e => e.instructorId && e.status === 'assigned');

          // Get existing threads first
          const existingThreads = await chatService.getChatThreads(userId, role);

          // Create threads for each enrollment with an instructor that doesn't have a thread
          for (const enrollment of activeEnrollments) {
            if (enrollment.instructorId) {
              const hasThread = existingThreads.some(thread => 
                thread.participants.instructor.id === enrollment.instructorId
              );

              if (!hasThread) {
                try {
                  await chatService.createChatThread(
                    enrollment.id,
                    userId,
                    `${enrollment.studentName}`,
                    enrollment.instructorId,
                    enrollment.instructorName || 'Instructor'
                  );
                } catch (error) {
                  console.error('Error creating chat thread:', error);
                }
              }
            }
          }

          // Get all threads including newly created ones
          const allThreads = await chatService.getChatThreads(userId, role);
          
          // Group threads by instructor ID
          const threadsByInstructor = allThreads.reduce<Record<string, ChatThread[]>>((acc, thread) => {
            const instructorId = thread.participants.instructor.id;
            if (!acc[instructorId]) {
              acc[instructorId] = [];
            }
            acc[instructorId].push(thread);
            return acc;
          }, {});

          // For each instructor, keep only the thread with the most recent message
          chatThreads = Object.values(threadsByInstructor).map(instructorThreads => {
            return instructorThreads.reduce((latest, current) => {
              const latestTime = latest.lastMessageTime ? new Date(latest.lastMessageTime).getTime() : 0;
              const currentTime = current.lastMessageTime ? new Date(current.lastMessageTime).getTime() : 0;
              return currentTime > latestTime ? current : latest;
            });
          });
        } else if (role === 'instructor') {
          // For instructors, get all threads and merge those from the same student
          const allThreads = await chatService.getChatThreads(userId, role);
          
          // Group threads by student ID
          const threadsByStudent = allThreads.reduce<Record<string, ChatThread[]>>((acc, thread) => {
            const studentId = thread.participants.student.id;
            if (!acc[studentId]) {
              acc[studentId] = [];
            }
            acc[studentId].push(thread);
            return acc;
          }, {});

          // For each student, keep only the thread with the most recent message
          chatThreads = Object.values(threadsByStudent).map(studentThreads => {
            return studentThreads.reduce((latest, current) => {
              const latestTime = latest.lastMessageTime ? new Date(latest.lastMessageTime).getTime() : 0;
              const currentTime = current.lastMessageTime ? new Date(current.lastMessageTime).getTime() : 0;
              return currentTime > latestTime ? current : latest;
            });
          });
        }

        // Sort threads by last message time
        chatThreads.sort((a, b) => {
          const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
          const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
          return timeB - timeA;
        });

        setThreads(chatThreads);
      } catch (error) {
        console.error('Error loading chat threads:', error);
      } finally {
        setLoading(false);
      }
    };

    loadThreads();
  }, [userId, role]);

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/4 bg-gray-200 rounded" />
                  <div className="h-4 w-3/4 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (threads.length === 0) {
    return (
      <Card className="h-full">
        <CardContent className="p-4">
          <div className="text-center text-muted-foreground">
            {role === 'student' 
              ? 'No active conversations. You will see your instructor here once assigned.'
              : 'No active conversations with students.'}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Messages</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="space-y-1">
          {threads.map((thread) => {
            const participant = role === 'student' 
              ? thread.participants.instructor 
              : thread.participants.student;
            
            return (
              <button
                key={thread.id}
                onClick={() => navigate(`/chat/${thread.id}`)}
                className="w-full p-4 hover:bg-muted/50 transition-colors flex items-center gap-4"
              >
                <Avatar>
                  <AvatarImage src={`https://ui-avatars.com/api/?name=${participant.name}`} />
                  <AvatarFallback>{participant.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <p className="font-medium truncate">{participant.name}</p>
                    {thread.lastMessageTime && (
                      <span className="text-xs text-muted-foreground">
                        {formatMessageTime(thread.lastMessageTime)}
                      </span>
                    )}
                  </div>
                  {thread.lastMessage && (
                    <p className="text-sm text-muted-foreground truncate">
                      {thread.lastMessage}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}; 